import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { WS_URL } from "./api";

const AuthCtx = createContext(null);

/**
 * AuthProvider — httpOnly-cookie based session.
 *
 * The backend sets an `access_token` httpOnly cookie on login/register/OAuth
 * and clears it on logout. Because the frontend + backend share the same
 * origin (via the k8s ingress), the browser attaches the cookie to every
 * axios call and every same-origin WebSocket handshake automatically. No
 * tokens are stored in localStorage, so XSS can no longer exfiltrate them.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsClient, setWsClient] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me — AuthCallback handles it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  // Open WS once authenticated. The auth cookie is sent automatically on
  // the same-origin WS handshake. Auto-reconnect with backoff + a 25s
  // heartbeat ping so the socket survives idle timeouts (k8s ingress
  // closes idle sockets after ~60s).
  useEffect(() => {
    if (!user?.user_id) return;
    let ws = null;
    let pingTimer = null;
    let reconnectTimer = null;
    let attempt = 0;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      try { ws = new WebSocket(WS_URL); }
      catch { scheduleReconnect(); return; }
      setWsClient(ws);

      ws.addEventListener("open", () => {
        attempt = 0;
        clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          try { ws.readyState === 1 && ws.send(JSON.stringify({ type: "ping" })); }
          catch { /* socket already dead — onclose will reconnect */ }
        }, 25_000);
      });

      const onClose = () => {
        clearInterval(pingTimer);
        if (cancelled) return;
        scheduleReconnect();
      };
      ws.addEventListener("close", onClose);
      ws.addEventListener("error", () => { try { ws.close(); } catch { /* noop */ } });
    };

    const scheduleReconnect = () => {
      clearTimeout(reconnectTimer);
      const delay = Math.min(15_000, 500 * Math.pow(2, attempt++));
      reconnectTimer = setTimeout(connect, delay);
    };

    connect();
    return () => {
      cancelled = true;
      clearInterval(pingTimer);
      clearTimeout(reconnectTimer);
      try { ws && ws.close(); } catch { /* noop */ }
    };
  }, [user?.user_id]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(null);
  };

  // OAuth callback helper: the AuthCallback page hits /auth/google/session
  // (or similar) which sets the httpOnly cookie server-side. We only need
  // to stash the user in state — no token to persist client-side.
  const setSession = (u) => setUser(u);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, setSession, fetchMe, wsClient }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
